import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import LogRocket from 'logrocket';
import PropTypes from 'prop-types';
import buttonStyles from '@utilities/components/Button/_index.module.scss';
import { Button, DownloadIcon, Error, Select, toast, truncateTextWithEllipsis } from '@utilities';
import { exportRawData } from '@api/survey_dashboard';
import { SEARCH_PARAM_KEYS } from '../../../Survey/utilities/constants';

import AskWhyResponses from './components/AskWhyResponses';
import styles from './_index.module.scss';

const AskWhyTab = ({ askWhyInstantSurveys }) => {
  const reportOptions = useMemo(
    () =>
      (askWhyInstantSurveys || []).map(({ id, publicUrl, title }) => ({
        label: truncateTextWithEllipsis({ length: 40, text: title }),
        value: { id, publicUrl, title },
      })),
    [askWhyInstantSurveys]
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedAskWhySurveyIdParam = searchParams.get(SEARCH_PARAM_KEYS.ASK_WHY_SURVEY_ID);
  const [selectedReport, setSelectedReport] = useState(() => reportOptions[0] ?? null);

  useEffect(() => {
    if (!reportOptions.length) {
      setSelectedReport(null);
      if (!selectedAskWhySurveyIdParam) return;
      const params = new URLSearchParams(searchParams);
      params.delete(SEARCH_PARAM_KEYS.ASK_WHY_SURVEY_ID);
      setSearchParams(params, { replace: true });
      return;
    }

    if (selectedAskWhySurveyIdParam) {
      const matchedOption = reportOptions.find(
        (option) => `${option.value?.id}` === selectedAskWhySurveyIdParam
      );

      if (matchedOption) {
        setSelectedReport((prev) =>
          prev?.value?.id === matchedOption.value?.id ? prev : matchedOption
        );
        return;
      }
    }

    const fallback = reportOptions[0];
    if (!fallback) return;

    setSelectedReport((prev) => (prev?.value?.id === fallback.value?.id ? prev : fallback));

    if (fallback.value?.id && fallback.value.id !== selectedAskWhySurveyIdParam) {
      const params = new URLSearchParams(searchParams);
      params.set(SEARCH_PARAM_KEYS.ASK_WHY_SURVEY_ID, fallback.value.id);
      setSearchParams(params, { replace: true });
    }
  }, [reportOptions, searchParams, selectedAskWhySurveyIdParam, setSearchParams]);

  const handleReportChange = useCallback(
    (nextReport) => {
      setSelectedReport(nextReport);
      const params = new URLSearchParams(searchParams);

      if (nextReport?.value?.id) {
        params.set(SEARCH_PARAM_KEYS.ASK_WHY_SURVEY_ID, nextReport.value.id);
      } else {
        params.delete(SEARCH_PARAM_KEYS.ASK_WHY_SURVEY_ID);
      }

      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    LogRocket.track('AskWhy_Tab');
  }, []);

  const selectedReportSurveyId = selectedReport?.value?.id;

  const handleDownloadRawData = useCallback(async () => {
    try {
      await exportRawData({ surveyId: selectedReportSurveyId });
    } catch (error) {
      console.error('[AskWhyTab] Raw data export failed', error);
      toast.error('Error occurred while exporting raw data.');
    }
  }, [selectedReportSurveyId]);

  if (!reportOptions.length) {
    return <Error status={404} />;
  }

  return (
    <div className={styles['ask-why-tab']}>
      <div className={styles['ask-why-tab-reports']}>
        <Select
          label="Reports"
          onChange={handleReportChange}
          options={reportOptions}
          value={selectedReport}
        />
        {!selectedReport?.value?.publicUrl && (
          <Button
            icon={<DownloadIcon />}
            isDisabled={!selectedReportSurveyId}
            onClick={handleDownloadRawData}
            text="Download Raw Data"
            variant="secondary"
          />
        )}
      </div>

      {!selectedReport?.value?.publicUrl && selectedReportSurveyId && (
        <AskWhyResponses surveyId={selectedReportSurveyId} />
      )}

      {selectedReport?.value?.publicUrl && (
        <>
          <div className={styles['ask-why-tab-link']}>
            <a
              className={buttonStyles['button-secondary']}
              href={selectedReport.value.publicUrl}
              rel="noreferrer"
              target="_blank"
            >
              Open as New Tab
            </a>
          </div>
          <iframe
            className={styles['ask-why-tab-iframe']}
            height="1200"
            src={selectedReport.value.publicUrl}
            title={selectedReport.value.title}
          />
        </>
      )}
    </div>
  );
};

AskWhyTab.propTypes = {
  askWhyInstantSurveys: PropTypes.array,
};

export default AskWhyTab;
